<template>
  <div class="ledger-page pb-5 pb-md-2">
    <div
      v-if="loading"
      class="text-center py-3"
    >
      <b-spinner />
    </div>
    <template v-else-if="row">
      <b-card class="rounded-lg">
        <div class="d-flex justify-content-between flex-wrap">
          <div>
            <h3 class="mb-50">
              {{ row.name }}
            </h3>
            <div class="text-muted">
              {{ row.specialty }}
            </div>
            <div class="mt-50">
              <small class="text-muted d-block">{{ $t('ledger_gl_account') }}</small>
              <strong>{{ glText }}</strong>
            </div>
          </div>
          <div class="d-none d-md-flex">
            <b-button
              v-if="canEdit"
              size="sm"
              variant="outline-primary"
              class="rounded-pill mr-50"
              @click="editOpen = true"
            >
              {{ $t('ledger_sub_edit') }}
            </b-button>
            <b-button
              v-if="canPay"
              size="sm"
              variant="success"
              class="rounded-pill mr-50"
              @click="openPay"
            >
              {{ $t('ledger_sub_pay') }}
            </b-button>
            <b-button
              v-if="canEdit"
              size="sm"
              variant="outline-danger"
              class="rounded-pill"
              @click="confirmDelete"
            >
              {{ $t('ledger_assets_delete') }}
            </b-button>
          </div>
        </div>
        <div class="ledger-sub-kpis mt-1">
          <div><span class="text-muted">2115</span><strong class="ledger-num d-block">{{ money(row.payable_balance) }}</strong></div>
          <div><span class="text-muted">2120</span><strong class="ledger-num d-block">{{ money(row.retention_balance) }}</strong></div>
        </div>
      </b-card>
      <b-card class="rounded-lg">
        <b-tabs>
          <b-tab :title="$t('ledger_sub_bills')">
            <article
              v-for="bill in bills"
              :key="bill.id"
              class="ledger-sub-card"
            >
              <b-link :to="{ name: 'ledger-subcontract-bills-view', params: { id: bill.id } }">
                {{ bill.bill_number }}
              </b-link>
              <div class="ledger-num">
                {{ $t('ledger_inv_balance') }} {{ money(bill.balance_due) }}
              </div>
            </article>
          </b-tab>
          <b-tab :title="$t('ledger_inv_payments')">
            <article
              v-for="payment in payments"
              :key="payment.id"
              class="ledger-sub-card"
            >
              <div>{{ payment.payment_date }} · {{ enumLabel(payment.kind, $i18n.locale) }}</div>
              <div class="small">
                {{ $t('ledger_pay_from', { from: paymentChart(payment) }) }}
              </div>
              <div class="small">
                {{ $t('ledger_pay_to', { to: glText }) }}
              </div>
              <div class="ledger-num">
                {{ money(payment.amount) }}
              </div>
            </article>
          </b-tab>
          <b-tab :title="$t('ledger_sub_statement')">
            <subcontractor-statement :subcontractor-id="row.id" />
          </b-tab>
        </b-tabs>
      </b-card>
    </template>

    <b-modal
      v-model="editOpen"
      :title="$t('ledger_sub_edit')"
      hide-footer
    >
      <subcontractor-form
        :subcontractor="row"
        @saved="onEdited"
      />
    </b-modal>
    <b-modal
      v-model="payOpen"
      :title="$t('ledger_sub_pay')"
      hide-footer
    >
      <p class="text-muted small">
        {{ $t('ledger_sub_pay_hint', { code: partyCode }) }}
      </p>
      <b-form-group :label="$t('ledger_payroll_till')">
        <b-form-select
          v-model="pay.account_id"
          :options="tillOptions"
        />
      </b-form-group>
      <b-form-group :label="$t('ledger_sub_bill')">
        <b-form-select
          v-model="pay.subcontract_bill_id"
          :options="billOptions"
        />
      </b-form-group>
      <b-form-group :label="$t('ledger_assets_amount')">
        <b-form-input
          v-model.number="pay.amount"
          type="number"
          min="0"
          step="0.01"
        />
      </b-form-group>
      <b-form-group :label="$t('ledger_date')">
        <b-form-input
          v-model="pay.payment_date"
          type="date"
        />
      </b-form-group>
      <b-form-group :label="$t('ledger_assets_notes')">
        <b-form-input v-model="pay.notes" />
      </b-form-group>
      <b-button
        variant="primary"
        class="rounded-pill"
        :disabled="acting || !canSubmitPay"
        @click="submitPay"
      >
        {{ $t('ledger_sub_pay') }}
      </b-button>
    </b-modal>
    <div
      v-if="row && canPay"
      class="ledger-sub-sticky d-md-none"
    >
      <b-button
        variant="success"
        size="sm"
        class="rounded-pill"
        @click="openPay"
      >
        {{ $t('ledger_sub_pay') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import {
  BCard, BButton, BSpinner, BTabs, BTab, BLink, BModal, BFormGroup, BFormSelect, BFormInput,
} from 'bootstrap-vue'
import Swal from 'sweetalert2'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { fetchAllOperationalAccounts } from '@/api/accounting/accounts'
import {
  getSubcontractor, deleteSubcontractor, paySubcontractor, getSubcontractBills,
} from '@/api/accounting/subcontractors'
import {
  listFrom, oneFrom, enumValue, enumLabel, formatLedgerMoney, roundMoney, todayIso,
  apiErrorMessage, glAccountText, glAccountCode,
} from '@/utils/ledger'
import SubcontractorForm from './SubcontractorForm.vue'
import SubcontractorStatement from './SubcontractorStatement.vue'

export default {
  name: 'SubcontractorShow',
  components: {
    BCard, BButton, BSpinner, BTabs, BTab, BLink, BModal, BFormGroup, BFormSelect, BFormInput, SubcontractorForm, SubcontractorStatement,
  },
  data() {
    return {
      loading: false,
      acting: false,
      row: null,
      bills: [],
      tills: [],
      editOpen: false,
      payOpen: false,
      pay: { account_id: null, amount: null, payment_date: todayIso(), subcontract_bill_id: null, notes: '' },
    }
  },
  computed: {
    canEdit() {
      return this.hasPermission('edit subcontractors')
    },
    glText() {
      return glAccountText(this.row) || '—'
    },
    partyCode() {
      return glAccountCode(this.row) || '—'
    },
    canPay() {
      return this.hasPermission('pay subcontractors')
    },
    payments() {
      return this.bills.flatMap(bill => bill.payments || [])
    },
    openBills() {
      return this.bills.filter(bill => {
        const status = enumValue(bill.status)
        return status === 'posted' || status === 'partially_paid'
      })
    },
    dueCap() {
      if (this.pay.subcontract_bill_id) {
        const bill = this.openBills.find(item => item.id === this.pay.subcontract_bill_id)
        return Number(bill && bill.balance_due) || 0
      }
      return Number(this.row && this.row.payable_balance) || 0
    },
    canSubmitPay() {
      const amount = Number(this.pay.amount)
      return this.pay.account_id && amount > 0 && amount <= this.dueCap + 0.001
    },
    tillOptions() {
      return [{ value: null, text: this.$t('ledger_payroll_till') }].concat(this.tills.map(account => ({
        value: account.id,
        text: account.name || account.code,
      })))
    },
    billOptions() {
      return [{ value: null, text: this.$t('ledger_sub_oldest') }].concat(this.openBills.map(bill => ({
        value: bill.id,
        text: bill.bill_number,
      })))
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    enumLabel,
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon', variant },
      })
    },
    async load() {
      this.loading = true
      try {
        const res = await getSubcontractor(this.$route.params.id)
        this.row = oneFrom(res)
        const billsRes = await getSubcontractBills({ subcontractor_id: this.row.id, per_page: 100 })
        this.bills = listFrom(billsRes)
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loading = false
      }
    },
    onEdited(saved) {
      this.editOpen = false
      if (saved) this.row = { ...this.row, ...saved }
    },
    async openPay() {
      this.pay = {
        account_id: null,
        amount: Number(this.row.payable_balance) || 0,
        payment_date: todayIso(),
        subcontract_bill_id: null,
        notes: '',
      }
      this.payOpen = true
      if (this.tills.length) return
      try {
        const rows = await fetchAllOperationalAccounts({ status: 'active', per_page: 100 })
        this.tills = rows.filter(account => ['cash', 'bank', 'petty_cash'].includes(enumValue(account.type)))
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      }
    },
    selectedTill() {
      return (this.tills || []).find(till => String(till.id) === String(this.pay.account_id)) || null
    },
    paymentChart(payment) {
      return glAccountText(payment && payment.account) || '—'
    },
    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
    },
    async submitPay() {
      if (!this.canSubmitPay) {
        this.toast(this.$t('ledger_sub_overpay'), 'warning')
        return
      }
      const bill = this.openBills.find(item => item.id === this.pay.subcontract_bill_id)
      const lines = [
        this.$t('ledger_pay_from', { from: this.escapeHtml(glAccountText(this.selectedTill()) || '—') }),
        this.$t('ledger_pay_to', { to: this.escapeHtml(this.glText) }),
      ]
      if (bill && bill.bill_number) lines.push(this.escapeHtml(bill.bill_number))
      lines.push(`${this.escapeHtml(this.money(this.pay.amount))} · ${this.escapeHtml(this.pay.payment_date || '')}`)
      const result = await Swal.fire({
        title: this.$t('ledger_sub_pay'),
        html: lines.map(line => `<div>${line}</div>`).join(''),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_sub_pay'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-outline-secondary ml-1' },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      this.acting = true
      try {
        const body = {
          account_id: this.pay.account_id,
          amount: roundMoney(this.pay.amount),
          payment_date: this.pay.payment_date,
          notes: String(this.pay.notes || '').trim() || undefined,
        }
        if (this.pay.subcontract_bill_id) body.subcontract_bill_id = this.pay.subcontract_bill_id
        await paySubcontractor(this.row.id, body)
        this.payOpen = false
        this.toast(this.$t('ledger_sub_paid'), 'success')
        await this.load()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      } finally {
        this.acting = false
      }
    },
    async confirmDelete() {
      const result = await Swal.fire({
        text: this.$t('ledger_sub_delete_confirm'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_assets_delete'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-outline-secondary ml-1' },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      try {
        await deleteSubcontractor(this.row.id)
        this.$router.push({ name: 'ledger-subcontractors' })
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      }
    },
  },
}
</script>

<style scoped>
.ledger-num { font-variant-numeric: tabular-nums; }
.ledger-sub-kpis { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }
.ledger-sub-card { border-bottom: 1px solid #eee; padding: 0.75rem 0; }
.ledger-sub-sticky {
  position: fixed; bottom: 0; inset-inline: 0; z-index: 20; background: #fff;
  border-top: 1px solid #e6e6e6; padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
}
</style>
